import { Component, OnInit } from '@angular/core';
import { BreadcrumbService, ProductService } from 'src/app/services';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { AddEnvironmentOverrideRequest, EnvironmentOverride,
  GetEnvironmentOverrideRequest, GetEnvironmentOverrideResponse,
  SituRenderServiceWebApiClient, AddEnvironmentOverrideResponse, UpdateEnvironmentOverrideRequest, ProductName } from 'siturenderservice-webapi-client';

@Component({
  selector: 'overrides-add-edit',
  templateUrl: './overrides-add-edit.component.html',
  styleUrls: ['./overrides-add-edit.component.less']
})

export class OverridesAddEditComponent implements OnInit {
  product: string = '';
  environment: string = '';
  warpEnvironmentId: string = '';
  id: string = '';

  overrideForm: FormGroup;
  submitted = false;
  isAddMode: boolean = false;
  isDarkMode: boolean = true;

  override: EnvironmentOverride | undefined;

  private _subscriptions: Subscription[] = [];

  private _loading = false;
  public get loading(): boolean { return this._loading; }

  public set loading(val: boolean) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val);
  }
  
  get form() { return this.overrideForm.controls; }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private situRenderClient: SituRenderServiceWebApiClient,
    private formBuilder: FormBuilder,
    private snackBar: MatSnackBar,
    private breadcrumbService: BreadcrumbService,
    private productService: ProductService) {
    this.overrideForm = this.formBuilder.group({
      id: [],
      warpEnvironmentId: [],
      callerReferenceKey: [null, Validators.required],
      version: [null, Validators.required]
    });
  }

  ngOnInit(): void {
    this.product = this.route.snapshot.queryParams['product'];
    this.environment = this.route.snapshot.queryParams['environment'];
    this.warpEnvironmentId = this.route.snapshot.queryParams['warpEnvironmentId'];
    this.id = this.route.snapshot.queryParams['overrideId'];

    this.overrideForm.controls.warpEnvironmentId.setValue(this.warpEnvironmentId);
    this.overrideForm.controls.id.setValue(this.id);

    this.isAddMode = !this.id;

    if(!this.isAddMode){
      this.loadOverride();
    }
  }

  onSubmit() {
    this.submitted = true;

    if (this.overrideForm.invalid) {
      this.submitted = false;
      return;
    }

    if (this.isAddMode) {
      this.addOverride();
    } else {
      this.editOverride();
    }
  }

  resetOverride() {
    if (this.isAddMode) {
      this.overrideForm.reset();
    } else {
      this.overrideForm.reset();
      this.loadOverride();
    }
  }

  showEnvironmentView() {
    this.router.navigate(['/admin/environments'], {
      queryParams: {
        product: this.product,
        environment: this.environment
      }
    });
  }

  private loadOverride(): void {
    this.loading = true;

    let request = new GetEnvironmentOverrideRequest();
    request.environmentOverrideId = this.id;
    this.situRenderClient.getEnvironmentOverride(request)
      .then((response: GetEnvironmentOverrideResponse) => {
        if (response.success && response.environmentOverride) {
          this.override = response.environmentOverride;
          this.overrideForm.patchValue(response.environmentOverride);
        } else {
          this.showMessage("There was an issue retrieving override!");
        }
        this.loading = false;
      })
      .catch((err: any) => {
        this.showMessage("Could not retrieve override!");
        this.loading = false;
      });
  }
  
  private editOverride() {
    let request = new UpdateEnvironmentOverrideRequest();
    request.environmentOverrideId = this.overrideForm.controls.id.value;
    request.callerReferenceKey = this.overrideForm.controls.callerReferenceKey.value;
    request.version = this.overrideForm.controls.version.value;
    
    this.situRenderClient.updateEnvironmentOverride(request)
      .then(response => {
        if (response.success) {
          this.showMessage(`Success! Override ${request.callerReferenceKey} was updated.`);
          this.showEnvironmentView();
        } else {
          this.showMessage(`Sorry we were unable to save your override ${request.callerReferenceKey}. Please try again!`);
        }
      })
      .catch((x: any) => {
        this.showMessage(`Sorry we were unable to save your override ${request.callerReferenceKey}. Please try again!`);
      });
  }

  private addOverride() {
    let request = new AddEnvironmentOverrideRequest();
    request.warpEnvironmentId = this.overrideForm.controls.warpEnvironmentId.value;
    request.callerReferenceKey = this.overrideForm.controls.callerReferenceKey.value;
    request.version = this.overrideForm.controls.version.value;

    this.situRenderClient.addEnvironmentOverride(request)
      .then(response => {
        if (response.success) {
          this.showMessage(`Success! Override for ${request.callerReferenceKey} was added.`);
          this.showEnvironmentView();
        } else {
          this.showMessage(`Sorry we were unable to add your override for ${request.callerReferenceKey}. Please try again!`);
        }
      })
      .catch((x: any) => {
        this.showMessage(`Sorry we were unable to add your override for ${request.callerReferenceKey}. Please try again!`);
      });
  }

  private showMessage(message: string) {
    this.snackBar.open(message, 'OK', { duration: 2000 });
  }
}
